import { reverse } from 'named-urls'
import React from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { PermissionGuardedRoute, useDashboardFeatureFlags } from '@components'
import { Dashboard } from '@components/common'
import * as S from '@components/common/Dashboard/Dashboard.styled'
import { ShopStatusDropdownDesktop } from '@components/common/Dashboard/DashboardShopQuickActions'
import { ShopQuickActionsMobile } from '@components/common/Dashboard/DashboardShopQuickActions/ShopQuickActionsMobile/ShopQuickActionsMobile'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { NavigateWithSearch } from '@components/utils/NavigateWithSearch'
import { Redirect } from '@components/utils/Redirect'
import {
  Button,
  Colors,
  DashboardLogo,
  Icon,
  ScrollToTop,
  ToolbarButton,
  useMediaQueryContext,
} from '@fourthwall/components'
import { CloseIcon, PlusIcon } from '@fourthwall/icons'
import { ShopStatusDropdown } from '@fourthwall/module-shop-status-switch'
import { lazyWithRetry } from '@fourthwall/utils/dist/lazyWithRetry'
import { useAdminModeContext } from '@modules/App/providers/AdminModeProvider'
import { AppRedirect } from '@modules/AppRedirect'
import { ConnectIntegration } from '@modules/ConnectIntegration'
import CollectionContainer from '@modules/Dashboard/Collection/CollectionContainer'
import { Home } from '@modules/Dashboard/Home/Home'
import { LaunchStrategyContinueBar } from '@modules/Dashboard/modules/LaunchStrategy'
import { ProfileDropdown } from '@modules/Dashboard/modules/ProfileDropdown'
import { SidebarContent } from '@modules/Dashboard/modules/SidebarContent'
import { Account } from '@modules/Dashboard/pages/Account'
import { Analytics } from '@modules/Dashboard/pages/Analytics/Analytics'
import { REPORT_TYPES } from '@modules/Dashboard/pages/Analytics/consts'
import { AnalyticsOverview } from '@modules/Dashboard/pages/Analytics/pages/AnalyticsOverview'
import { AnalyticsReport } from '@modules/Dashboard/pages/Analytics/pages/AnalyticsOverview/pages/AnalyticsReport'
import { AnalyticsTrackingPixels } from '@modules/Dashboard/pages/Analytics/pages/AnalyticsOverview/pages/AnalyticsTrackingPixels'
import { AnalyticsReports } from '@modules/Dashboard/pages/Analytics/pages/AnalyticsReports'
import { AnalyticsRouteWrapper } from '@modules/Dashboard/pages/AnalyticsV2/AnalyticsRouteWrapper'
import { AnalyticsDashboard } from '@modules/Dashboard/pages/AnalyticsV2/pages/AnalyticsDashboard/AnalyticsDashboard'
import { AnalyticsTrackingPixels as AnalyticsTrackingPixelsV2 } from '@modules/Dashboard/pages/AnalyticsV2/pages/AnalyticsDashboard/pages/AnalyticsTrackingPixels/AnalyticsTrackingPixels'
import { AnalyticsReport as AnalyticsReportV2 } from '@modules/Dashboard/pages/AnalyticsV2/pages/AnalyticsReport/AnalyticsReport'
import { AnalyticsReports as AnalyticsReportsV2 } from '@modules/Dashboard/pages/AnalyticsV2/pages/AnalyticsReports/AnalyticsReports'
import { AppsContainer as SettingsAppsContainer } from '@modules/Dashboard/pages/Apps/AppsContainer'
import { AppsBeehiiv } from '@modules/Dashboard/pages/Apps/pages/AppsBeehiiv'
import { AppsBookfunnel } from '@modules/Dashboard/pages/Apps/pages/AppsBookfunnel'
import { AppsDiscord } from '@modules/Dashboard/pages/Apps/pages/AppsDiscord'
import { AppsDiscordVerification } from '@modules/Dashboard/pages/Apps/pages/AppsDiscordVerification'
import { AppsInstagramFeed } from '@modules/Dashboard/pages/Apps/pages/AppsInstagramFeed'
import { AppsKlaviyo } from '@modules/Dashboard/pages/Apps/pages/AppsKlaviyo'
import { AppsLaylo } from '@modules/Dashboard/pages/Apps/pages/AppsLaylo'
import { AppsLinktree } from '@modules/Dashboard/pages/Apps/pages/AppsLinktree'
import { AppsMailchimp } from '@modules/Dashboard/pages/Apps/pages/AppsMailchimp'
import { AppsMetaConversions } from '@modules/Dashboard/pages/Apps/pages/AppsMetaConversions'
import { AppsShipStation } from '@modules/Dashboard/pages/Apps/pages/AppsShipStation'
import { AppsSpotify } from '@modules/Dashboard/pages/Apps/pages/AppsSpotify'
import { Podcast } from '@modules/Dashboard/pages/Apps/pages/AppsSpotify/modules/Podcast'
import { AppsStreamelementsContainer } from '@modules/Dashboard/pages/Apps/pages/AppsStreamelements'
import { AppsStreamlabsContainer } from '@modules/Dashboard/pages/Apps/pages/AppsStreamlabs'
import { AppsTiktok } from '@modules/Dashboard/pages/Apps/pages/AppsTiktok'
import { AppsTiktokShop } from '@modules/Dashboard/pages/Apps/pages/AppsTiktokShop'
import { AppsTwitchContainer } from '@modules/Dashboard/pages/Apps/pages/AppsTwitch'
import { AppsTwitchDiscounts } from '@modules/Dashboard/pages/Apps/pages/AppsTwitchDiscounts'
import { AppsTwitterShops } from '@modules/Dashboard/pages/Apps/pages/AppsTwitterShops'
import { AppsVimmi } from '@modules/Dashboard/pages/Apps/pages/AppsVimmi'
import { AppsZapier } from '@modules/Dashboard/pages/Apps/pages/AppsZapier'
import { Billing } from '@modules/Dashboard/pages/Settings/pages/Billing'
import { Transactions } from '@modules/Dashboard/pages/Settings/pages/Billing/pages/Transactions'
import { Checkout } from '@modules/Dashboard/pages/Settings/pages/Checkout'
import { Domain } from '@modules/Dashboard/pages/Settings/pages/Domain'
import { DomainConfigurationContainer } from '@modules/Dashboard/pages/Settings/pages/Domain/pages/DomainConfiguration/DomainConfigurationContainer'
import { NewDomain } from '@modules/Dashboard/pages/Settings/pages/Domain/pages/NewDomain/NewDomainContainer'
import { ForDevelopers } from '@modules/Dashboard/pages/Settings/pages/ForDevelopers'
import { General as GeneralSettings } from '@modules/Dashboard/pages/Settings/pages/General'
import { Plan } from '@modules/Dashboard/pages/Settings/pages/Plan/Plan'
import { PlatformAppEdit } from '@modules/Dashboard/pages/Settings/pages/PlatformApps/PlatformAppEdit'
import { PlatformApps } from '@modules/Dashboard/pages/Settings/pages/PlatformApps/PlatformApps'
import { Referrals } from '@modules/Dashboard/pages/Settings/pages/Referrals'
import { Samples } from '@modules/Dashboard/pages/Settings/pages/Samples'
import { SamplesOrder } from '@modules/Dashboard/pages/Settings/pages/Samples/pages/SamplesOrder'
import { Shipping } from '@modules/Dashboard/pages/Settings/pages/Shipping'
import { ShippingGroup } from '@modules/Dashboard/pages/Settings/pages/Shipping/pages/ShippingGroup'
import { Team } from '@modules/Dashboard/pages/Settings/pages/Team'
import { ThankYouCards } from '@modules/Dashboard/pages/Settings/pages/ThankYouCards/ThankYouCards'
import { routing } from '@utils/routing'

import { OmnisearchToolbarButton } from '../AIKit/Omnisearch'
import { OfferPreview } from '../OfferPreview/OfferPreview'
import { RecirectV2ToV1 } from '../ThemeEditorV2/RecirectV2ToV1'
import { useShopTime } from '../Timezone/providers/ShopTimeProvider'
import { Collections } from './Collections/Collections'
import { Member } from './Memberships/modules/Member/Member'
import { Members } from './Memberships/modules/Members/Members'
import { Overview } from './Memberships/modules/Overview/Overview'
import { Perks } from './Memberships/modules/Perks/Perks'
import { Tier, TierCreate } from './Memberships/modules/Perks/modules/Tier'
import { Post } from './Memberships/modules/Post/Post'
import { PostCreate } from './Memberships/modules/Post/PostCreate'
import { Posts } from './Memberships/modules/Posts/Posts'
import { Series } from './Memberships/modules/Series/Series'
import { Settings as MembershipsSettings } from './Memberships/modules/Settings'
import { Badges } from './Memberships/modules/Settings/modules/Badges/Badges'
import { Badge, BadgeCreate } from './Memberships/modules/Settings/modules/Badges/modules/Badge'
import { CustomEmoji } from './Memberships/modules/Settings/modules/CustomEmoji/CustomEmoji'
import { General as MembershipsGeneralSettings } from './Memberships/modules/Settings/modules/General/General'
import { Messages } from './Memberships/modules/Settings/modules/Messages/Messages'
import { MobileApp } from './Memberships/modules/Settings/modules/MobileApp/MobileApp'
import { PostTags } from './Memberships/modules/Settings/modules/PostTags/PostTags'
import { Upselling } from './Memberships/modules/Settings/modules/Upselling/Upselling'
import { WelcomePost } from './Memberships/modules/Settings/modules/WelcomePost/WelcomePost'
import { SingleSeries } from './Memberships/modules/SingleSeries/SingleSeries'
import { SingleSeriesCreate } from './Memberships/modules/SingleSeries/SingleSeriesCreate'
import { Costs } from './Product/ProductCosts/Costs'
import { ProductSectionsContainer } from './Product/ProductSections'
import { ProductSettings } from './Product/ProductSettings'
import { ProductVariantsContainer } from './Product/ProductVariants'
import { SingleProduct } from './Product/SingleProduct/SingleProductContainer'
import { ProductsContainer } from './Products/ProductsContainer'
import { useMenuDrawerContext } from './contexts/MenuDrawerContext'
import { useBanners } from './hooks/useBanners'
import { useNavigations } from './hooks/useNavigations'
import { MessagesWidget } from './modules/MessagesWidget'
import { QuickActionsDropdown } from './modules/QuickActionsDropdown'
import { AppsKit } from './pages/Apps/pages/AppsKit'
import { AppsKitAuthorize } from './pages/Apps/pages/AppsKit/AppsKitAuthorize'
import { AppsMeta } from './pages/Apps/pages/AppsMeta/AppsMeta'
import { AppsPlatformUnified } from './pages/Apps/pages/AppsPlatformUnified'
import { AppsPledge } from './pages/Apps/pages/AppsPledge'
import { Fulfillments } from './pages/Contributions/pages/Fulfillments'
import { Orders } from './pages/Contributions/pages/Orders'
import { Donation } from './pages/Contributions/pages/Orders/pages/Donation'
import { Order } from './pages/Contributions/pages/Orders/pages/Order'
import { OrderCancel } from './pages/Contributions/pages/Orders/pages/Order/pages/OrderCancel'
import { OrderEdit } from './pages/Contributions/pages/Orders/pages/Order/pages/OrderEdit'
import { OrderRefundContainer } from './pages/Contributions/pages/Orders/pages/Order/pages/OrderRefund/OrderRefundContainer'
import { ThankYous } from './pages/Contributions/pages/ThankYous'
import { Promotions } from './pages/Promotions'
import { GiftCard } from './pages/Promotions/pages/GiftCard'
import { GiftCards } from './pages/Promotions/pages/GiftCards'
import { GiveawayPackages } from './pages/Promotions/pages/GiveawayPackages'
import { PromotionCodes } from './pages/Promotions/pages/PromotionCodes'
import type { DashboardViewProps } from './types'

const GiveawayContainer = lazyWithRetry(
  () => import('./pages/Contributions/pages/Orders/pages/Giveaway'),
)
const ThemeEditorV2 = lazyWithRetry(
  () => import('../ThemeEditorV2/modules/ThemeEditor/ThemeEditorContainer'),
)

const PromotionCodeContainer = lazyWithRetry(() => import('./pages/Promotions/pages/PromotionCode'))
const MembershipsPromotion = lazyWithRetry(
  () => import('./pages/Promotions/pages/MembershipsPromotion'),
)
const GiveawayPackageContainer = lazyWithRetry(
  () => import('./pages/Promotions/pages/GiveawayPackage'),
)
const Settings = lazyWithRetry(() => import('@modules/Dashboard/pages/Settings/Settings'))
const BlockedTransactionsContainer = lazyWithRetry(
  () => import('@modules/Dashboard/pages/BlockedTransactions'),
)

const AppsYouTubeProductShelfContainer = lazyWithRetry(
  () => import('@modules/Dashboard/pages/Apps/pages/AppsYouTubeProductShelf'),
)
const MembershipsMessages = lazyWithRetry(() => import('./Memberships/modules/Messages/Messages'))

const InventoryShipmentCreate = lazyWithRetry(
  () =>
    import(
      '@modules/Dashboard/pages/Settings/pages/Shipping/pages/InventoryShipments/pages/InventoryShipmentCreate'
    ),
)
const InventoryShipmentsShow = lazyWithRetry(
  () =>
    import(
      '@modules/Dashboard/pages/Settings/pages/Shipping/pages/InventoryShipments/pages/InventoryShipmentsShow'
    ),
)
const InventoryStockSnapshot = lazyWithRetry(
  () =>
    import(
      '@modules/Dashboard/pages/Settings/pages/Shipping/pages/InventoryStocks/InventoryStocks'
    ),
)
const WebViewRecommendations = lazyWithRetry(
  () => import('@modules/Dashboard/pages/WebView/pages/WebViewRecommendations'),
)
const BundleProduct = lazyWithRetry(
  () => import('@modules/Dashboard/Product/BundleProduct/BundleProduct'),
)
const CombinedProduct = lazyWithRetry(
  () => import('@modules/Dashboard/Product/CombinedProduct/CombinedProduct'),
)
const GiftCardProduct = lazyWithRetry(
  () => import('@modules/Dashboard/Product/GiftCardProduct/GiftCardProduct'),
)

export const DashboardView = ({
  shopName,
  user,
  partnerDashboard,
  connectedShops,
}: DashboardViewProps) => {
  const location = useLocation()
  const { hasAnyPermission } = useDashboardPermissions()
  const { navigations, getFirstVisibleNavigation } = useNavigations()
  const { bannersProps } = useBanners()
  const firstVisibleNavigation = getFirstVisibleNavigation()
  const admin = useAdminModeContext()
  const { drawerClose } = useMenuDrawerContext()
  const { timezone } = useShopTime()
  // openChatbot is now accessed via DashboardOmnisearch component

  const media = useMediaQueryContext()

  const toolbarIconSize = media.laptop ? 18 : 20
  const avatarLabel = admin?.isActive ? 'admin' : undefined
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const analyticsV2Routes = dashboardFeatureFlags.analyticsV2.enabled ? (
    <Route
      path={`${routing.analyticsV2.self}*`}
      element={
        <PermissionGuardedRoute permission="analytics" element={<AnalyticsRouteWrapper />} />
      }
    >
      <Route
        index
        element={<Navigate to={getFirstVisibleNavigation(routing.analyticsV2.self)} replace />}
      />
      <Route
        path="overview"
        element={<PermissionGuardedRoute permission="analytics" element={<AnalyticsDashboard />} />}
      />
      <Route path="overview/tracking-pixels" element={<AnalyticsTrackingPixelsV2 />} />
      <Route
        path="reports"
        element={<PermissionGuardedRoute permission="analytics" element={<AnalyticsReportsV2 />} />}
      />
      <Route
        path="reports/:reportId"
        element={<PermissionGuardedRoute permission="analytics" element={<AnalyticsReportV2 />} />}
      />
    </Route>
  ) : null

  const routes = (
    <Routes>
      <Route
        path={routing.redirect.googleAnalytics}
        element={<NavigateWithSearch to={routing.analytics.self} />}
      />
      <Route path={routing.redirect.meta} element={<NavigateWithSearch to={routing.apps.meta} />} />
      <Route
        path={routing.redirect.streamlabs}
        element={<NavigateWithSearch to={routing.apps.streamlabs} />}
      />
      <Route
        path={routing.redirect.twitch}
        element={<NavigateWithSearch to={routing.apps.twitch} />}
      />
      <Route
        path={routing.redirect.youtube}
        element={<NavigateWithSearch to={routing.apps.youTubeProductShelf} />}
      />
      <Route
        path={routing.home}
        element={
          firstVisibleNavigation !== routing.home ? (
            <Navigate to={firstVisibleNavigation} replace />
          ) : (
            <PermissionGuardedRoute permission="home" element={<Home />} />
          )
        }
      />
      <Route
        path={`${routing.analytics.self}*`}
        element={<PermissionGuardedRoute permission="analytics" element={<Analytics />} />}
      >
        <Route
          index
          element={<Navigate to={getFirstVisibleNavigation(routing.analytics.self)} replace />}
        />
        <Route path="overview" element={<AnalyticsOverview />} />
        <Route path="overview/tracking-pixels" element={<AnalyticsTrackingPixels />} />
        <Route path="overview/:reportType" element={<AnalyticsReport />} />
        <Route path="reports" element={<AnalyticsReports />} />
      </Route>
      <Route
        path={routing.analytics.reportContributions}
        element={
          <Navigate
            to={reverse(routing.analytics.overview.report.self, {
              reportType: REPORT_TYPES.orders.slug,
            })}
            replace
          />
        }
      />
      <Route
        path={routing.analytics.report}
        element={<Redirect to={routing.analytics.overview.report.self} />}
      />
      {analyticsV2Routes}
      <Route
        path={routing.apps.self}
        element={<PermissionGuardedRoute permission="apps" element={<SettingsAppsContainer />} />}
      />
      <Route
        path={routing.membershipsBeta.self}
        element={<NavigateWithSearch to={routing.memberships.overview} />}
      />
      <Route
        path={routing.membershipsEnable.self}
        element={<NavigateWithSearch to={routing.memberships.overview} />}
      />
      <Route
        path={routing.products.self}
        element={<Navigate to={routing.products.all.self} replace />}
      />
      <Route
        path={routing.products.all.self}
        element={
          <PermissionGuardedRoute permission="products.view" element={<ProductsContainer />} />
        }
      />
      <Route
        path={routing.products.combined.combinedId.self}
        element={
          <PermissionGuardedRoute
            // TODO: change to actual permision later
            permission="products.edit.details"
            element={<CombinedProduct />}
          />
        }
      />
      <Route
        path={routing.products.bundle.bundleId.self}
        element={
          <PermissionGuardedRoute
            // TODO: change to actual permision later
            permission="products.create"
            element={<BundleProduct />}
          />
        }
      />
      <Route
        path={routing.products.giftCard.giftCardId.self}
        element={
          <PermissionGuardedRoute
            // TODO: change to actual permision later
            permission="products.create"
            element={<GiftCardProduct />}
          />
        }
      />
      <Route
        path={routing.products.all.product.self}
        element={<PermissionGuardedRoute permission="products.view" element={<SingleProduct />} />}
      />
      <Route
        path={routing.products.all.product.variants}
        element={
          <PermissionGuardedRoute
            permission="products.edit.details"
            element={<ProductVariantsContainer />}
          />
        }
      />
      <Route
        path={routing.products.all.product.gallery}
        element={<Navigate to="../" relative="path" replace />}
      />
      <Route
        path={routing.products.all.product.sections}
        element={
          <PermissionGuardedRoute
            permission="products.edit.additionalSections"
            element={<ProductSectionsContainer />}
          />
        }
      />
      <Route
        path={routing.products.all.product.settings}
        element={
          <PermissionGuardedRoute
            permission="products.advancedSettings"
            element={<ProductSettings />}
          />
        }
      />
      <Route
        path={routing.products.all.product.costs}
        element={<PermissionGuardedRoute roleName="ROLE_COSTS_WRITE" element={<Costs />} />}
      />
      <Route
        path={routing.contributions.self}
        element={<Navigate to={routing.contributions.orders.all} replace />}
      />
      <Route
        path={routing.contributions.orders.order.self}
        element={
          <PermissionGuardedRoute
            // permission="contributions.orders.view"
            roleName="ROLE_ORDER_READ"
            element={<Order />}
          />
        }
      />
      <Route
        path={routing.contributions.orders.order.edit}
        element={
          <PermissionGuardedRoute permission="contributions.orders.edit" element={<OrderEdit />} />
        }
      />
      <Route
        path={routing.contributions.orders.order.cancel}
        element={
          <PermissionGuardedRoute
            permission="contributions.orders.cancel"
            element={<OrderCancel />}
          />
        }
      />
      <Route
        path={routing.contributions.orders.order.refund}
        element={
          <PermissionGuardedRoute
            permission="contributions.orders.refund"
            element={<OrderRefundContainer />}
          />
        }
      />
      <Route
        path={routing.contributions.orders.all}
        element={
          <PermissionGuardedRoute
            // permission="contributions.orders.view"
            roleName="ROLE_ORDER_READ"
            element={<Orders />}
          />
        }
      />
      <Route
        path={routing.contributions.orders.donations.all}
        element={
          <PermissionGuardedRoute permission="contributions.orders.view" element={<Orders />} />
        }
      />
      <Route
        path={routing.contributions.orders.giveaways.all}
        element={
          <PermissionGuardedRoute permission="contributions.orders.view" element={<Orders />} />
        }
      />
      <Route
        path={routing.contributions.orders.donations.donation.self}
        element={
          <PermissionGuardedRoute permission="contributions.orders.view" element={<Donation />} />
        }
      />
      <Route
        path={routing.contributions.fulfillment}
        element={
          <PermissionGuardedRoute
            permission="contributions.fulfillments.view"
            element={<Fulfillments />}
          />
        }
      />
      <Route
        path={routing.contributions.thankYous.self}
        element={
          <PermissionGuardedRoute permission="contributions.thankYous" element={<ThankYous />} />
        }
      />
      <Route
        path={routing.contributions.orders.giveaways.giveaway}
        element={
          <PermissionGuardedRoute
            permission="contributions.orders.view"
            element={<GiveawayContainer />}
          />
        }
      />
      <Route
        path={routing.promotions.self}
        element={<Navigate to={routing.promotions.promotionCodes.self} replace />}
      />
      <Route
        path={routing.promotions.promotionCodes.self}
        element={
          <PermissionGuardedRoute
            permission="promotions.shopPromoCodes"
            element={<Promotions content={<PromotionCodes />} />}
          />
        }
      />
      <Route
        path={routing.promotions.giveawayPackages.self}
        element={
          <PermissionGuardedRoute
            permission="promotions.giveawayLinks"
            element={<Promotions content={<GiveawayPackages />} />}
          />
        }
      />
      <Route
        path={routing.promotions.giveawayPackages.create.self}
        element={
          <PermissionGuardedRoute
            permission="promotions.giveawayLinks"
            element={<GiveawayPackageContainer />}
          />
        }
      />
      <Route
        path={routing.promotions.giveawayPackages.giveawayPackage.self}
        element={
          <PermissionGuardedRoute
            permission="promotions.giveawayLinks"
            element={<GiveawayPackageContainer />}
          />
        }
      />
      <Route
        path={routing.promotions.promotionCodes.create.self}
        element={
          <PermissionGuardedRoute
            permission="promotions.shopPromoCodes"
            element={<PromotionCodeContainer />}
          />
        }
      />
      <Route
        path={routing.promotions.promotionCodes.promotionCode.self}
        element={
          <PermissionGuardedRoute
            permission="promotions.shopPromoCodes"
            element={<PromotionCodeContainer />}
          />
        }
      />
      <Route
        path={routing.promotions.promotionCodes.membershipsPromotion.self}
        element={
          <PermissionGuardedRoute
            permission="promotions.membershipsPromoCodes"
            element={<MembershipsPromotion />}
          />
        }
      />
      <Route
        path={routing.promotions.promotionCodes.membershipsPromotionCreate.self}
        element={
          <PermissionGuardedRoute
            permission="promotions.membershipsPromoCodes"
            element={<MembershipsPromotion />}
          />
        }
      />
      <Route
        path={routing.promotions.giftCards.self}
        element={
          <PermissionGuardedRoute
            permission="promotions.giftCards"
            element={<Promotions content={<GiftCards />} />}
          />
        }
      />
      <Route
        path={routing.promotions.giftCards.create.self}
        element={
          <PermissionGuardedRoute permission="promotions.giftCards" element={<GiftCard />} />
        }
      />
      <Route
        path={routing.promotions.giftCards.giftCard.self}
        element={
          <PermissionGuardedRoute permission="promotions.giftCards" element={<GiftCard />} />
        }
      />
      <Route
        path={routing.products.collections.self}
        element={<PermissionGuardedRoute permission="collections.view" element={<Collections />} />}
      />
      <Route
        path={routing.products.collections.create.self}
        element={
          <PermissionGuardedRoute
            permission="collections.create"
            element={<CollectionContainer />}
          />
        }
      />
      <Route
        path={routing.products.collections.collection.self}
        element={
          <PermissionGuardedRoute permission="collections.view" element={<CollectionContainer />} />
        }
      />
      <Route
        path={routing.settings.self}
        element={<Navigate to={routing.settings.general} replace />}
      />
      <Route
        path={routing.settings.general}
        element={
          <PermissionGuardedRoute
            permission="settings.general"
            element={<Settings element={<GeneralSettings />} />}
          />
        }
      />
      <Route
        path={routing.settings.team}
        element={
          <PermissionGuardedRoute
            permission="settings.team.view"
            element={<Settings element={<Team />} />}
          />
        }
      />
      <Route path={routing.settings.appsLegacy.app} element={<Redirect to={routing.apps.app} />} />
      <Route
        path={routing.apps.twitchMembershipsDiscountsLegacy}
        element={<Redirect to={routing.apps.twitchMembershipsDiscounts} />}
      />
      <Route
        path={routing.settings.shipping.self}
        element={
          <PermissionGuardedRoute
            permission="settings.shipping"
            element={<Settings element={<Shipping />} />}
          />
        }
      />
      <Route
        path={routing.settings.shipping.group.self}
        element={
          <PermissionGuardedRoute permission="settings.shipping" element={<ShippingGroup />} />
        }
      />
      <Route
        path={routing.settings.shipping.group.create}
        element={
          <PermissionGuardedRoute permission="settings.shipping" element={<ShippingGroup />} />
        }
      />
      <Route
        path={routing.settings.shipping.inventoryShipments.self}
        element={
          <PermissionGuardedRoute
            permission="settings.shipping"
            element={<InventoryShipmentsShow />}
          />
        }
      />
      <Route
        path={routing.settings.shipping.inventoryShipments.create}
        element={
          <PermissionGuardedRoute
            permission="settings.shipping"
            element={<InventoryShipmentCreate />}
          />
        }
      />
      <Route
        path={routing.settings.shipping.inventoryStocks.self}
        element={
          <PermissionGuardedRoute
            permission="settings.shipping"
            element={<InventoryStockSnapshot />}
          />
        }
      />
      <Route
        path={routing.settings.checkout}
        element={
          <PermissionGuardedRoute
            permission="settings.checkout"
            element={<Settings element={<Checkout />} />}
          />
        }
      />
      <Route
        path={routing.settings.forDevelopers}
        element={
          <PermissionGuardedRoute
            permission="settings.forDevelopers"
            element={<Settings element={<ForDevelopers />} />}
          />
        }
      />
      <Route
        path={routing.settings.platformApps.self}
        element={
          <PermissionGuardedRoute
            permission="settings.platformApps"
            element={<Settings element={<PlatformApps />} />}
          />
        }
      />
      <Route
        path={routing.settings.platformApps.editApp.self}
        element={
          <PermissionGuardedRoute
            permission="settings.platformApps"
            element={<PlatformAppEdit />}
          />
        }
      />
      <Route
        path={routing.settings.domain.self}
        element={
          <PermissionGuardedRoute
            permission="settings.domain"
            element={<Settings element={<Domain />} />}
          />
        }
      />
      <Route
        path={routing.settings.domain.create}
        element={<PermissionGuardedRoute permission="settings.domain" element={<NewDomain />} />}
      />
      <Route
        path={routing.settings.domain.configuration}
        element={
          <PermissionGuardedRoute
            permission="settings.domain"
            element={<DomainConfigurationContainer />}
          />
        }
      />
      <Route
        path={routing.settings.billing.self}
        element={
          <PermissionGuardedRoute
            permission="settings.billing.view"
            element={<Settings element={<Billing />} />}
          />
        }
      />
      <Route
        path={routing.settings.plan}
        element={
          <PermissionGuardedRoute
            permission="subscriptions.view"
            element={<Settings element={<Plan />} />}
          />
        }
      />
      <Route
        path={routing.settings.samples.self}
        element={
          <PermissionGuardedRoute
            permission="settings.samples.view"
            element={<Settings element={<Samples />} />}
          />
        }
      />
      <Route
        path={routing.settings.referrals.self}
        element={
          <PermissionGuardedRoute
            permission="settings.referrals"
            element={<Settings element={<Referrals />} />}
          />
        }
      />
      <Route
        path={routing.settings.thankYouCards.self}
        element={
          <PermissionGuardedRoute
            permission="settings.thankYouCards.view"
            element={<Settings element={<ThankYouCards />} />}
          />
        }
      />
      <Route
        path={routing.apps.streamlabs}
        element={<PermissionGuardedRoute permission="apps" element={<AppsStreamlabsContainer />} />}
      />
      <Route
        path={routing.apps.twitch}
        element={<PermissionGuardedRoute permission="apps" element={<AppsTwitchContainer />} />}
      />
      <Route
        path={routing.apps.twitchMembershipsDiscounts}
        element={<PermissionGuardedRoute permission="apps" element={<AppsTwitchDiscounts />} />}
      />
      <Route
        path={routing.apps.tiktok}
        element={<PermissionGuardedRoute permission="apps" element={<AppsTiktok />} />}
      />
      <Route
        path={routing.apps.tiktokShop}
        element={<PermissionGuardedRoute permission="apps" element={<AppsTiktokShop />} />}
      />
      <Route
        path={routing.apps.twitterShops}
        element={<PermissionGuardedRoute permission="apps" element={<AppsTwitterShops />} />}
      />
      <Route
        path={routing.apps.streamelements}
        element={
          <PermissionGuardedRoute permission="apps" element={<AppsStreamelementsContainer />} />
        }
      />
      <Route
        path={routing.apps.instagramFeed}
        element={<PermissionGuardedRoute permission="apps" element={<AppsInstagramFeed />} />}
      />
      <Route
        path={routing.apps.spotify.self}
        element={<PermissionGuardedRoute permission="apps" element={<AppsSpotify />} />}
      />
      <Route
        path={routing.apps.spotify.podcast.self}
        element={<PermissionGuardedRoute permission="apps" element={<Podcast />} />}
      />
      <Route
        path={routing.apps.discord}
        element={<PermissionGuardedRoute permission="apps" element={<AppsDiscord />} />}
      />
      <Route
        path={routing.apps.discordVerification}
        element={<PermissionGuardedRoute permission="apps" element={<AppsDiscordVerification />} />}
      />
      <Route
        path={routing.apps.youTubeProductShelf}
        element={
          <PermissionGuardedRoute
            permission="apps"
            element={<AppsYouTubeProductShelfContainer />}
          />
        }
      />
      <Route
        path={routing.apps.youTubeProductShelfLegacy}
        element={<Redirect to={routing.apps.youTubeProductShelf} />}
      />
      <Route
        path={routing.apps.shipStation}
        element={<PermissionGuardedRoute permission="apps" element={<AppsShipStation />} />}
      />
      <Route
        path={routing.apps.klaviyo}
        element={<PermissionGuardedRoute permission="apps" element={<AppsKlaviyo />} />}
      />
      <Route
        path={routing.apps.beehiiv}
        element={<PermissionGuardedRoute permission="apps" element={<AppsBeehiiv />} />}
      />
      <Route
        path={routing.apps.bookfunnel}
        element={<PermissionGuardedRoute permission="apps" element={<AppsBookfunnel />} />}
      />
      <Route path={routing.apps.packiyo} element={<AppsPlatformUnified appId="apps.packiyo" />} />
      <Route
        path={routing.apps.merchtrain}
        element={<AppsPlatformUnified appId="apps.merchtrain" />}
      />
      <Route
        path={routing.apps.laylo}
        element={<PermissionGuardedRoute permission="apps" element={<AppsLaylo />} />}
      />
      <Route path={routing.apps.linktree} element={<AppsLinktree />} />
      <Route path={routing.apps.mailchimp} element={<AppsMailchimp />} />
      <Route path={routing.apps.kit.self} element={<AppsKit />} />
      <Route path={routing.apps.kit.authorize} element={<AppsKitAuthorize />} />
      <Route path={routing.apps.meta} element={<AppsMeta />} />
      <Route path={routing.apps.metaCAPI} element={<AppsMetaConversions />} />
      <Route path={routing.apps.pledge} element={<AppsPledge />} />
      <Route path={routing.apps.zapier} element={<AppsZapier />} />
      <Route
        path={routing.apps.echobrain}
        element={<AppsPlatformUnified appId="apps.echobrain" />}
      />
      <Route path={routing.apps.vimmi} element={<AppsVimmi />} />
      <Route path={routing.apps.app} element={<AppsPlatformUnified />} />
      <Route path={routing.apps.redirect.appConnect} element={<AppRedirect />} />
      <Route
        path={routing.settings.billing.transactions}
        element={
          <PermissionGuardedRoute
            permission="settings.billing.history.full"
            element={<Transactions />}
          />
        }
      />
      <Route
        path={routing.settings.samples.order}
        element={
          <PermissionGuardedRoute permission="settings.samples.create" element={<SamplesOrder />} />
        }
      />
      <Route
        path={`${routing.account.self}*`}
        element={<PermissionGuardedRoute permission="accountSettings" element={<Account />} />}
      />
      <Route
        path={routing.memberships.self}
        element={<Navigate to={getFirstVisibleNavigation(routing.memberships.self)} replace />}
      />
      <Route
        path={routing.memberships.overview}
        element={
          <PermissionGuardedRoute permission="memberships.overview" element={<Overview />} />
        }
      />
      <Route
        path={routing.memberships.posts.self}
        element={<PermissionGuardedRoute permission="memberships.post.view" element={<Posts />} />}
      />
      <Route
        path={routing.memberships.posts.scheduled.all}
        element={<PermissionGuardedRoute permission="memberships.post.view" element={<Posts />} />}
      />
      <Route
        path={routing.memberships.posts.create}
        element={
          <PermissionGuardedRoute permission="memberships.post.create" element={<PostCreate />} />
        }
      />
      <Route
        path={routing.memberships.posts.post.self}
        element={<PermissionGuardedRoute permission="memberships.post.create" element={<Post />} />}
      />
      <Route
        path={routing.memberships.series.self}
        element={
          <PermissionGuardedRoute permission="memberships.video.view" element={<Series />} />
        }
      />
      <Route
        path={routing.memberships.series.create}
        element={
          <PermissionGuardedRoute
            permission="memberships.video.create"
            element={<SingleSeriesCreate />}
          />
        }
      />
      <Route
        path={routing.memberships.series.singleSeries.self}
        element={
          <PermissionGuardedRoute
            permission="memberships.video.create"
            element={<SingleSeries />}
          />
        }
      />
      <Route
        path={routing.memberships.members.self}
        element={<PermissionGuardedRoute permission="memberships.members" element={<Members />} />}
      />
      <Route
        path={routing.memberships.members.member.self}
        element={<PermissionGuardedRoute permission="memberships.members" element={<Member />} />}
      />
      <Route
        path={routing.memberships.perks.self}
        element={
          <PermissionGuardedRoute permission="memberships.perksAndTiers" element={<Perks />} />
        }
      />
      <Route
        path={routing.memberships.perks.create}
        element={
          <PermissionGuardedRoute permission="memberships.perksAndTiers" element={<TierCreate />} />
        }
      />
      <Route
        path={routing.memberships.perks.tier.self}
        element={
          <PermissionGuardedRoute permission="memberships.perksAndTiers" element={<Tier />} />
        }
      />
      <Route
        path={`${routing.memberships.messages.self}*`}
        element={
          <PermissionGuardedRoute
            permission="memberships.messages"
            element={<MembershipsMessages />}
          />
        }
      />
      <Route
        path={routing.memberships.settings.self}
        element={<Navigate to={routing.memberships.settings.general} replace />}
      />
      <Route
        path={routing.memberships.settings.general}
        element={
          <PermissionGuardedRoute
            permission="memberships.settings.general"
            element={<MembershipsSettings element={<MembershipsGeneralSettings />} />}
          />
        }
      />
      <Route
        path={routing.memberships.settings.messages}
        element={
          <PermissionGuardedRoute
            permission="memberships.settings.messages"
            element={<MembershipsSettings element={<Messages />} />}
          />
        }
      />
      <Route
        path={routing.memberships.settings.badges.self}
        element={
          <PermissionGuardedRoute
            permission="memberships.settings.badges"
            element={<MembershipsSettings element={<Badges />} />}
          />
        }
      />
      <Route
        path={routing.memberships.settings.customEmoji}
        element={
          <PermissionGuardedRoute
            permission="memberships.settings.customEmoji"
            element={<MembershipsSettings element={<CustomEmoji />} />}
          />
        }
      />
      <Route
        path={routing.memberships.settings.postTags}
        element={
          <PermissionGuardedRoute
            permission="memberships.settings.postTags"
            element={<MembershipsSettings element={<PostTags />} />}
          />
        }
      />
      <Route
        path={routing.memberships.settings.badges.create}
        element={
          <PermissionGuardedRoute
            permission="memberships.settings.badges"
            element={<BadgeCreate />}
          />
        }
      />
      <Route
        path={routing.memberships.settings.badges.badge.self}
        element={
          <PermissionGuardedRoute permission="memberships.settings.badges" element={<Badge />} />
        }
      />
      <Route
        path={routing.memberships.settings.welcomePost}
        element={
          <PermissionGuardedRoute
            permission="memberships.settings.welcomePost"
            element={<MembershipsSettings element={<WelcomePost />} />}
          />
        }
      />
      <Route
        path={routing.memberships.settings.mobileApp}
        element={
          <PermissionGuardedRoute
            permission="memberships.settings.mobileApp"
            element={<MembershipsSettings element={<MobileApp />} />}
          />
        }
      />
      <Route
        path={routing.memberships.settings.upselling}
        element={
          <PermissionGuardedRoute
            permission="memberships.settings.upselling"
            element={<MembershipsSettings element={<Upselling />} />}
          />
        }
      />
      <Route path={routing.webview.recommendations} element={<WebViewRecommendations />} />
      <Route path={routing.blockedTransactions} element={<BlockedTransactionsContainer />} />
    </Routes>
  )

  return (
    <Routes>
      <Route path={routing.connectIntegration} element={<ConnectIntegration />} />
      <Route
        path="*"
        element={
          <>
            <ScrollToTop location={location.pathname}>
              <Dashboard
                headerMobile={
                  <S.HeaderMobile>
                    <S.HeaderTopWrapperMobile>
                      <S.HeaderTopMobile>
                        <DashboardLogo name={shopName} to={routing.home} hideFwIcon />

                        <button onClick={drawerClose}>
                          <Icon component={CloseIcon} width={18} height={18} />
                        </button>
                      </S.HeaderTopMobile>

                      <ShopQuickActionsMobile />
                    </S.HeaderTopWrapperMobile>

                    <ShopStatusDropdown size="medium" position="bottom-start" timezone={timezone} />
                  </S.HeaderMobile>
                }
                toolbarLeftContentDesktop={
                  <S.HeaderLeftContent>
                    <DashboardLogo name={shopName} to={routing.home} maxWidth="200px" />

                    <S.ShopQuickActions>
                      <ShopStatusDropdownDesktop />
                    </S.ShopQuickActions>
                  </S.HeaderLeftContent>
                }
                toolbarCenterContent={<OmnisearchToolbarButton />}
                toolbarRightContent={
                  <S.ToolbarRightContent>
                    <QuickActionsDropdown>
                      {media.tablet ? (
                        <Button
                          label="Create"
                          appearance="secondary"
                          size="small"
                          leftIcon={<PlusIcon />}
                        />
                      ) : (
                        <ToolbarButton label="Create" icon={PlusIcon} iconSize={toolbarIconSize} />
                      )}
                    </QuickActionsDropdown>

                    <S.ActionIconsWrapper>
                      <MessagesWidget iconSize={toolbarIconSize} />
                    </S.ActionIconsWrapper>

                    <S.ToolbarSpacer />

                    <ProfileDropdown
                      user={user}
                      connectedShops={connectedShops}
                      partnerDashboardUrl={partnerDashboard?.url}
                      label={avatarLabel}
                      testid="profile"
                      avatarSize={media.laptop ? 'medium' : 'small'}
                      backgroundColor={media.laptop ? Colors.GS200 : Colors.GS100}
                    />
                  </S.ToolbarRightContent>
                }
                sidebarContent={
                  hasAnyPermission() ? <SidebarContent navigations={navigations} /> : undefined
                }
                drawerFooterContent={
                  <Dashboard.Menu>
                    <ProfileDropdown
                      user={user}
                      label={avatarLabel}
                      connectedShops={connectedShops}
                      partnerDashboardUrl={partnerDashboard?.url}
                      variant="menu-item"
                      testid="profile"
                      backgroundColor={Colors.GS0}
                    />
                  </Dashboard.Menu>
                }
                content={routes}
                bannersProps={hasAnyPermission() ? bannersProps : []}
              />
            </ScrollToTop>
            <LaunchStrategyContinueBar />
          </>
        }
      />
      <Route
        path={`${routing.themeEditorV2.self}*`}
        element={<PermissionGuardedRoute permission="themeEditor" element={<ThemeEditorV2 />} />}
      />
      <Route
        path="/admin/dashboard/store-design-v2/*"
        element={<PermissionGuardedRoute permission="themeEditor" element={<RecirectV2ToV1 />} />}
      />
      <Route path={routing.products.preview} element={<OfferPreview />} />
    </Routes>
  )
}
